import { baseApi } from '@/shared/api/baseApi.js';
import type { TeamListItem, TeamMember, TeamDetail, TeamProjectAssignment } from './teams.types.js';

export const teamsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTeams: builder.query<{ success: true; data: TeamListItem[] }, void>({
      query: () => '/teams',
      providesTags: ['Teams'],
    }),

    getTeam: builder.query<{ success: true; data: TeamDetail }, string>({
      query: (id) => `/teams/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Team', id }],
    }),

    createTeam: builder.mutation<
      { success: true; data: TeamDetail },
      { name: string; description?: string }
    >({
      query: (body) => ({ url: '/teams', method: 'POST', body }),
      invalidatesTags: ['Teams'],
    }),

    updateTeam: builder.mutation<
      { success: true; data: TeamDetail },
      { id: string; data: { name?: string; description?: string } }
    >({
      query: ({ id, data }) => ({ url: `/teams/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: (_r, _e, { id }) => ['Teams', { type: 'Team', id }, 'Conversations'],
    }),

    deleteTeam: builder.mutation<{ success: true; data: { id: string } }, string>({
      query: (id) => ({ url: `/teams/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Teams', 'Conversations'],
    }),

    addTeamMember: builder.mutation<
      { success: true; data: TeamMember },
      { teamId: string; userId: string; role?: string; specialization?: string }
    >({
      query: ({ teamId, ...body }) => ({
        url: `/teams/${teamId}/members`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { teamId }) => ['Teams', { type: 'Team', id: teamId }],
    }),

    updateTeamMember: builder.mutation<
      { success: true; data: TeamMember },
      { teamId: string; memberId: string; role?: string; specialization?: string }
    >({
      query: ({ teamId, memberId, ...body }) => ({
        url: `/teams/${teamId}/members/${memberId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { teamId }) => [{ type: 'Team', id: teamId }],
    }),

    removeTeamMember: builder.mutation<
      { success: true; data: { id: string } },
      { teamId: string; memberId: string }
    >({
      query: ({ teamId, memberId }) => ({
        url: `/teams/${teamId}/members/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { teamId }) => ['Teams', { type: 'Team', id: teamId }],
    }),

    assignTeamToProject: builder.mutation<
      { success: true; data: TeamProjectAssignment },
      { teamId: string; projectId: string }
    >({
      query: ({ teamId, projectId }) => ({
        url: `/teams/${teamId}/projects`,
        method: 'POST',
        body: { projectId },
      }),
      invalidatesTags: (_r, _e, { teamId }) => ['Teams', { type: 'Team', id: teamId }],
    }),

    unassignTeamFromProject: builder.mutation<
      { success: true },
      { teamId: string; projectId: string }
    >({
      query: ({ teamId, projectId }) => ({
        url: `/teams/${teamId}/projects/${projectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { teamId }) => ['Teams', { type: 'Team', id: teamId }],
    }),

    getPendingInvitations: builder.query<{ success: true; data: any[] }, void>({
      query: () => '/teams/invitations',
      providesTags: ['Teams'],
    }),

    acceptInvitation: builder.mutation<any, string>({
      query: (memberId) => ({ url: `/teams/invitations/${memberId}/accept`, method: 'POST' }),
      invalidatesTags: ['Teams'],
    }),

    declineInvitation: builder.mutation<any, string>({
      query: (memberId) => ({ url: `/teams/invitations/${memberId}/decline`, method: 'POST' }),
      invalidatesTags: ['Teams'],
    }),

    setMemberPermissions: builder.mutation<any, { teamId: string; data: any }>({
      query: ({ teamId, data }) => ({
        url: `/teams/${teamId}/permissions`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Teams'],
    }),

    getMemberPermissions: builder.query<
      { success: true; data: Record<string, any> },
      { teamId: string; userId: string }
    >({
      query: ({ teamId, userId }) => `/teams/${teamId}/permissions/${userId}`,
    }),
  }),
});

export const {
  useGetTeamsQuery,
  useGetTeamQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useAddTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useAssignTeamToProjectMutation,
  useUnassignTeamFromProjectMutation,
  useGetPendingInvitationsQuery,
  useAcceptInvitationMutation,
  useDeclineInvitationMutation,
  useSetMemberPermissionsMutation,
  useLazyGetMemberPermissionsQuery,
} = teamsApi;
